import * as THREE from "three"
import { useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { useIsMobile } from "../../../utils/useIsMobile"


export default function Train(props) {
  const train = useRef()
  const isMobile = useIsMobile()

  const {
    targetRef,
    count = 260,
    speedZ = 14,
    size = isMobile ? 20 : 30,
    ...rest
  } = props

  const positions = useMemo(() => new Float32Array(count * 3), [count])
  const alphas = useMemo(() => {
    const a = new Float32Array(count)
    for (let i = 0; i < count; i++) a[i] = 1 - i / (count - 1)
    return a
  }, [count])

  const tmp = useMemo(() => new THREE.Vector3(), [])
  const initialized = useRef(false)

  const accumulator = useRef(0)
  const stepTime = 1 / 240

  useFrame((_, dt) => {
    if (!train.current || !targetRef?.current) return
    targetRef.current.getWorldPosition(tmp)

    if (!initialized.current) {
        for (let i = 0; i < count; i++) {
            positions[i * 3 + 0] = tmp.x
            positions[i * 3 + 1] = tmp.y
            positions[i * 3 + 2] = tmp.z
        }
        initialized.current = true
        train.current.geometry.attributes.position.needsUpdate = true
        return
    }

    accumulator.current += Math.min(dt, 0.1)

    const dz = speedZ * stepTime
    let steps = 0
    const maxStepsPerFrame = 8

    while (accumulator.current >= stepTime && steps < maxStepsPerFrame) {
        accumulator.current -= stepTime
        steps++

        // A — recul en Z de toute la chaîne (sauf la tête)
        for (let i = 1; i < count; i++) {
            positions[i * 3 + 2] -= dz
        }

        // B — décalage : chaque point prend la position de son prédécesseur
        for (let i = count - 1; i > 0; i--) {
            positions[i * 3 + 0] = positions[(i - 1) * 3 + 0]
            positions[i * 3 + 1] = positions[(i - 1) * 3 + 1]
            positions[i * 3 + 2] = positions[(i - 1) * 3 + 2]
        }

        // C — la tête reprend la position de la source
        positions[0] = tmp.x
        positions[1] = tmp.y
        positions[2] = tmp.z
    }

    if (steps > 0) {
        train.current.geometry.attributes.position.needsUpdate = true
    }
})

  return (
    <points ref={train} frustumCulled={false} {...rest}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" array={positions} itemSize={3} count={count} />
        <bufferAttribute attach="attributes-aAlpha" array={alphas} itemSize={1} count={count} />
      </bufferGeometry>

      <shaderMaterial
        transparent
        depthWrite={false}
        depthTest={true}
        blending={THREE.AdditiveBlending}
        uniforms={{ uSize: { value: size } }}
        vertexShader={`
  attribute float aAlpha;
  varying float vAlpha;
  uniform float uSize;

  void main() {
    vAlpha = aAlpha;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    // Plus large au départ : tête plus “gonflée”
// seulement les ~10-15% premiers deviennent gros
    float head = smoothstep(0.85, 1.0, aAlpha);
    head = pow(head, 1.6);
    float minSize = uSize * 0.2;          // queue
    float maxSize = uSize * 4.0;          // tête (augmente ici)
    gl_PointSize = mix(minSize, maxSize, head);
  }
`}
        fragmentShader={`
    varying float vAlpha;

    void main() {
      vec2 uv = gl_PointCoord - vec2(0.5);
      float d = length(uv);

      float radius = 0.48;
      float borderSize = 0.10;

      if (d > radius) discard;

      // masques
      float border = step(radius - borderSize, d);
      float fill = 1.0 - border;

      // couleurs (tes teintes)
      vec3 top = vec3(1.0, 0.95, 0.25);
      vec3 mid = vec3(1.0, 0.35, 0.10);
      vec3 bot = vec3(0.85, 0.05, 0.05);

      // gradient interne
      float y = clamp(uv.y / radius * 0.5 + 0.5, 0.0, 1.0);
      vec3 base = mix(bot, mid, smoothstep(0.0, 0.55, y));
      base = mix(base, top, smoothstep(0.35, 1.0, y));

      // contour sombre (comics)
      vec3 borderColor = vec3(0.06, 0.0, 0.0);

      vec3 col = base * fill + borderColor * border;

      // anti-clipping pour additive
      col = 1.0 - exp(-col * 1.3);

      // alpha = disque + légère atténuation queue (pas obligatoire)
      float a = 0.55 * (0.25 + 0.75 * vAlpha);
gl_FragColor = vec4(col, a);
    }
  `}
      />
    </points>
  )
}